import posthog from "posthog-js";

import { wasErrorHandled } from "@/lib/posthog-error-dedup";

/**
 * Named capture rather than `posthog.captureException`: our self-hosted
 * PostHog silently drops `$exception` events — the error-tracking consumer
 * isn't deployed (see the TODO in `lib/error-logger.ts`). Verified against
 * the live project: `$exception` last landed 2026-07-16, while named events
 * ingest normally. A captureException here would go nowhere.
 */
export const API_REQUEST_FAILED_EVENT = "api_request_failed";

export type ApiFailureKind =
  /** Request never left the device — Safari "Load failed", DNS, offline. */
  | "network"
  /** Our own ky timeout fired. */
  | "timeout"
  /** Caller cancelled (unmount, navigation, explicit AbortSignal). */
  | "aborted"
  /** Client-side limiter short-circuited before the network. */
  | "rate_limit"
  /** Server answered with an error status. */
  | "http"
  /** Response arrived but wasn't JSON — proxy error page, truncated body. */
  | "parse"
  | "unknown";

export interface ApiFailurePayload {
  kind: ApiFailureKind;
  error_name: string;
  error_message: string;
  method: string;
  path: string;
  elapsed_ms: number;
  online: boolean | null;
  visibility_state: string | null;
  /** ms the tab has been open — stale-connection hypothesis predicts long-lived tabs. */
  page_age_ms: number | null;
  /**
   * ms since the tab last became visible. Safari has a documented bug where a
   * fetch issued just after a tab is revealed hangs for 20-40s; this is the
   * field that confirms or kills that hypothesis for a given failure.
   */
  visible_for_ms: number | null;
}

/**
 * Allowlist, not a blocklist. A segment is emitted only when it is
 * unmistakably a route word — alphabetic tokens, optionally with an
 * alphanumeric suffix (`clinic-setup`, `icd-10`, `checkout-v2`). Everything
 * else is masked, so an ID that happens to be alphabetic, short, hyphenated
 * or percent-encoded fails closed instead of leaking. Every one of the 177
 * literal endpoint segments in the app satisfies this, so masking-by-default
 * costs nothing in route readability.
 */
const ROUTE_SEGMENT_RE = /^[a-z]+(?:-[a-z0-9]+)*$/;

/** ms (page-relative) at which the tab last became visible; 0 = since load. */
let lastVisibleAt: number | null = null;

function hasPerformance(): boolean {
  return typeof performance !== "undefined";
}

if (typeof document !== "undefined") {
  lastVisibleAt = document.visibilityState === "visible" ? 0 : null;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      lastVisibleAt = hasPerformance() ? performance.now() : 0;
    }
  });
}

/**
 * Reduce an endpoint to a route template: query string and hash dropped, and
 * every segment that isn't a recognised route word masked. Patient,
 * appointment and tenant IDs must never reach telemetry, and counting
 * failures per route is what the payload is actually for.
 */
export function normalizeRequestPath(endpoint: string): string {
  const withoutQuery = endpoint.split(/[?#]/)[0];
  const segments = withoutQuery
    .split("/")
    .filter(Boolean)
    .map((segment) => (ROUTE_SEGMENT_RE.test(segment) ? segment : ":id"));
  return `/${segments.join("/")}`;
}

export function classifyApiFailure(error: unknown): ApiFailureKind {
  const name = error instanceof Error ? error.name : "";
  switch (name) {
    case "HTTPError":
      return "http";
    case "TimeoutError":
      return "timeout";
    case "AbortError":
      return "aborted";
    case "ClientRateLimitError":
      return "rate_limit";
    case "SyntaxError":
      return "parse";
    // fetch rejects with TypeError when the request never reaches the network.
    case "TypeError":
    case "NetworkError":
      return "network";
    default:
      return "unknown";
  }
}

const MESSAGE_LIMIT = 300;
const URL_RE = /\bhttps?:\/\/\S+/gi;

/**
 * Error messages are not safe to forward verbatim.
 *
 * - `JSON.parse` quotes the response body back at us (V8: `Unexpected token
 *   '<', "<html>Pati"...`), and that body is patient data — drop it entirely.
 * - ky's `HTTPError` embeds the full request URL, query string included
 *   (`Request failed with status code 500: POST https://…?q=…`). Those are
 *   normally deduped out before reaching here, but only because `beforeError`
 *   marked them; if that ever fails to run the message must still be clean.
 *
 * Everything else is truncated as a backstop against another engine
 * embedding content in a message.
 */
function safeMessage(error: unknown, kind: ApiFailureKind): string {
  if (kind === "parse") return "[omitted: may contain response body]";
  const message =
    error instanceof Error ? error.message : String(error ?? "unknown");
  return message.replace(URL_RE, "[url]").slice(0, MESSAGE_LIMIT);
}

export function buildApiFailurePayload(
  error: unknown,
  method: string,
  endpoint: string,
  elapsedMs: number,
): ApiFailurePayload {
  const err = error instanceof Error ? error : undefined;
  const kind = classifyApiFailure(error);
  return {
    kind,
    error_name: err?.name ?? typeof error,
    error_message: safeMessage(error, kind),
    method: method.toUpperCase(),
    path: normalizeRequestPath(endpoint),
    elapsed_ms: elapsedMs,
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    visibility_state:
      typeof document !== "undefined" ? document.visibilityState : null,
    // Both are page-relative, so they're only meaningful when `performance`
    // supplies a navigation-start origin — `Date.now()` would report epoch ms.
    page_age_ms: hasPerformance() ? Math.round(performance.now()) : null,
    visible_for_ms:
      lastVisibleAt === null || !hasPerformance()
        ? null
        : Math.round(performance.now() - lastVisibleAt),
  };
}

/**
 * Report an API request that failed. Only errors with no other trace are
 * captured: `beforeError` already reports HTTP failures (and the server logged
 * them), and the client rate limiter reports its own trips.
 */
export function reportApiFailure(
  error: unknown,
  method: string,
  endpoint: string,
  startedAt: number,
): void {
  try {
    if (classifyApiFailure(error) === "rate_limit") return;
    if (wasErrorHandled(error)) return;

    posthog.capture(
      API_REQUEST_FAILED_EVENT,
      buildApiFailurePayload(error, method, endpoint, Date.now() - startedAt),
    );
  } catch {
    // never let telemetry break the request flow
  }
}
